import { useEffect, useRef, useState } from 'react'
import { Check, Sparkles, X } from 'lucide-react'

import { TulipSketch } from '../../components/TulipSketch'
import { isCoarseRoute, routeState, viaCoords } from '../../lib/route'
import { tulipKm, tulipText } from '../../lib/tulip'
import { belagGeprueft } from '../../lib/surface'
import { mitAbsage, ohneAbsage, stelleSchluessel, vorschlagsLage } from '../../lib/tulipMerge'
import { tulipsEinfuegen, tulipSuggestions, type TulipSuggestion } from '../../lib/tulipSuggest'
import type { Stage, Tulip } from '../../lib/types'
import { useFetchTulipSource } from './routeApi'
import { routeErrorText } from './stageRouting'
import {
  ABDECKUNG_VOLLSTAENDIG,
  leerSatz,
  mitAbdeckungsvermerk,
  schonImBuchSatz,
  type Quellen,
} from './tulipHinweise'

/**
 * Tulpen vorschlagen lassen - und einzeln entscheiden, welche bleiben.
 *
 * Der Grund, dass es diesen Knopf gibt: fünfzehn Zeichen, eine Legende und ein
 * Druckbogen nützen nichts, solange jede Kreuzung von Hand einzutragen wäre,
 * mitsamt einem Kilometerstand, den vor der Fahrt niemand kennt. Der
 * Routendienst kennt beides - er hat die Strecke gerechnet und dabei jede
 * Kreuzung samt ihren Winkeln benannt.
 *
 * Geschrieben wird trotzdem nichts von selbst. Der Vorschlag ist eine Liste zum
 * Ansehen: je Zeile das Zeichen in Druckgrösse, der Kilometerstand und der
 * Hinweis, und daneben ein Haken. Wer nichts anhakt, bekommt nichts. Und was
 * schon in der Etappe steht, bleibt stehen - übernommen wird dazu, nicht statt.
 *
 * Die Liste hat ein Gedächtnis, und zwar im Dokument (lib/tulipMerge.ts). Sie
 * wird oft geholt - nach jeder Streckenänderung, nach jedem Belagsabruf -, und
 * ohne Gedächtnis stünde jedes Mal dasselbe da, vollständig angehakt: auch das,
 * was längst im Buch steht, und auch das, was jemand begründet weggeworfen hat.
 * Was übernommen wurde, taucht deshalb gar nicht mehr auf; was abgelehnt wurde,
 * steht ohne Haken da und lässt sich mit einem Tippen zurückholen.
 *
 * Die Kosten sind der Grund für den Knopf und nicht für eine Automatik: der
 * Routendienst erlaubt eine Anfrage je Sekunde. Meistens kostet der Vorschlag
 * gar nichts, weil die Manöver schon beim Berechnen der Strecke mitgekommen
 * sind; bei Strecken aus der Zeit davor kostet er genau eine Anfrage.
 */
export function TulipVorschlaege({
  stage,
  onChange,
}: {
  stage: Stage
  onChange: (patch: Partial<Stage>) => void
}) {
  const fetchSource = useFetchTulipSource()
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState('')
  const [vorschlaege, setVorschlaege] = useState<TulipSuggestion[] | null>(null)
  // Was die Tunnelsuche über sich selbst sagt: nicht nachgesehen, teilweise
  // nachgesehen, oder vollständig. Steht neben der Liste, damit ein fehlender
  // Tunnel nicht wie ein nicht vorhandener aussieht.
  const [abdeckung, setAbdeckung] = useState<number | null>(null)
  const abbruch = useRef<AbortController | null>(null)

  // Wechselt die Etappe oder ihre Strecke, ist eine offene Liste nicht mehr die
  // Liste dieser Etappe. Sie stehen zu lassen hiesse, Vorschläge für eine
  // andere Strasse anzubieten - und die sähen genauso aus wie richtige.
  const strecke = `${stage.id}:${String(stage.route?.computedAt ?? 0)}`
  useEffect(() => {
    setVorschlaege(null)
    setFehler('')
    setAbdeckung(null)
  }, [strecke])

  // Ein laufender Abruf gehört zu dieser Maske. Wer sie verlässt, hat die
  // Antwort nicht mehr bestellt.
  useEffect(() => () => abbruch.current?.abort(), [])

  const zustand = routeState(stage)

  async function holen() {
    abbruch.current?.abort()
    const steuer = new AbortController()
    abbruch.current = steuer

    setLaeuft(true)
    setFehler('')
    try {
      const quelle = await fetchSource(stage.c1, viaCoords(stage), stage.c2, steuer.signal)
      // Die Kehren kommen aus der Etappe und nicht aus dieser Antwort: gezählt
      // wurden sie auf der vollen Geometrie, als die Strecke berechnet wurde.
      // Die gespeicherte Linie hat davon noch 400 Punkte, und aus einem Punkt
      // liest niemand eine Haarnadel.
      const gefunden = tulipSuggestions(quelle.steps, {
        serpentines: stage.route?.serpentines ?? [],
        tunnels: quelle.tunnels?.found ?? [],
        // Der Belag kommt wie die Kehren aus der Etappe und nicht aus dieser
        // Antwort: er wird eigens nachgeschlagen (StageSurfacePanel), weil die
        // Abfrage Sekunden bis Minuten dauert. Wer sie noch nicht angestossen
        // hat, bekommt hier keine Schotterzeile - und der Satz darunter sagt,
        // dass das nicht dasselbe ist wie „kein Schotter“.
        surface: stage.route?.surface?.sections ?? [],
      })
      setVorschlaege(gefunden)
      setAbdeckung(quelle.tunnels ? quelle.tunnels.coverage : null)
    } catch (error) {
      if (steuer.signal.aborted) return
      setFehler(routeErrorText(error))
      setVorschlaege(null)
      setAbdeckung(null)
    } finally {
      if (!steuer.signal.aborted) setLaeuft(false)
    }
  }

  /**
   * Der Haken an einer Zeile - und damit das Ja oder Nein zu dieser Stelle.
   *
   * Geschrieben wird sofort ins Dokument und nicht erst beim Übernehmen. Ein
   * Nein, das nur in dieser Maske stünde, wäre beim nächsten Druck auf den
   * Knopf wieder weg - und genau das ist der Fehler, den es hier zu beheben
   * gab.
   */
  function umschalten(vorschlag: TulipSuggestion, jetztNein: boolean) {
    const abgesagt = stage.tulipsRejected ?? []
    const naechste = jetztNein ? mitAbsage(abgesagt, vorschlag) : ohneAbsage(abgesagt, vorschlag)
    onChange({ tulipsRejected: naechste })
  }

  function uebernehmen() {
    // Übernommen wird, was angehakt dasteht - und angehakt ist, was weder im
    // Buch steht noch abgelehnt wurde.
    const gewaehlteTulpen: Tulip[] = offen
      .filter((eintrag) => eintrag.lage === 'neu')
      .map(({ vorschlag }) => {
        // Die Herkunft geht mit ins Dokument. Nur daran erkennt der nächste
        // Lauf, dass diese Zeile schon im Buch steht - auch dann noch, wenn
        // unterwegs jemand ihren Kilometerstand berichtigt hat. Ein leeres Feld
        // wird gar nicht erst geschrieben: es stünde für eine Stelle, die es
        // nicht gibt.
        const src = stelleSchluessel(vorschlag)
        return {
          km: vorschlag.km,
          shape: vorschlag.shape,
          turn: vorschlag.turn,
          note: vorschlag.note,
          ...(src ? { src } : {}),
        }
      })
    if (gewaehlteTulpen.length === 0) return

    // Der Vorbehalt über die Kartendeckung geht mit ins Dokument. Vorher war
    // er flüchtiger Zustand dieser Maske: die Tunnelzeile kam ins Buch, die
    // Auskunft darüber, wie viel überhaupt nachgesehen wurde, nicht.
    const neue = mitAbdeckungsvermerk(gewaehlteTulpen, quellen)

    onChange({ tulips: tulipsEinfuegen(stage.tulips ?? [], neue) })
    setVorschlaege(null)
  }

  // Was von den Vorschlägen noch zu entscheiden ist. Die schon übernommenen
  // fallen ganz heraus: sie stehen unmittelbar darunter in der Roadbookliste,
  // und ein zweites Mal angeboten wären sie eine Einladung zur doppelten Zeile.
  const lage = vorschlagsLage(vorschlaege ?? [], stage.tulips ?? [], stage.tulipsRejected ?? [])
  const offen = (vorschlaege ?? [])
    .map((vorschlag, i) => ({ vorschlag, lage: lage[i] ?? 'neu' }))
    .filter((eintrag) => eintrag.lage !== 'drin')
  const schonDrin = (vorschlaege?.length ?? 0) - offen.length
  const anzahl = offen.filter((eintrag) => eintrag.lage === 'neu').length
  // Was gefragt werden konnte - dieselbe Auskunft für den Leertext, für die
  // Herkunftszeile und für den Vermerk in den übernommenen Tulpen. Getrennte
  // Bedingungen an drei Stellen waren genau der Fehler: der Leertext verneinte
  // den Tunnel kategorisch, und die Herkunftszeile darunter sagte, dass gar
  // nicht gesucht wurde.
  const quellen: Quellen = {
    kehren: !isCoarseRoute(stage),
    abdeckung,
    ...(stage.route?.surface ? { belag: stage.route.surface } : {}),
  }

  return (
    <section className="inhalt-block tulpen-vorschlag">
      <h5 className="block-titel">
        <Sparkles size={13} /> Tulpen aus der Strecke
      </h5>

      {zustand !== 'current' ? (
        <p className="leer">
          {zustand === 'none'
            ? 'Vorschläge kommen aus der berechneten Strecke. Erst die Strecke dieser Etappe berechnen lassen.'
            : 'Die gespeicherte Strecke passt nicht mehr zu Start, Ziel oder Zwischenpunkten. Erst neu berechnen lassen.'}
        </p>
      ) : (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf still"
            disabled={laeuft}
            onClick={() => void holen()}
          >
            <Sparkles size={14} /> {laeuft ? 'Sucht Kreuzungen …' : 'Tulpen vorschlagen'}
          </button>
          {vorschlaege && (
            /*
              „Schliessen“ und nicht mehr „Verwerfen“: verworfen wird seit dem
              Gedächtnis zeilenweise mit dem Haken, und der bleibt. Dieser Knopf
              klappt nur die Liste zu - ein Wort, das beides meinte, hiesse an
              der einen Stelle „nie wieder“ und an der anderen „nicht jetzt“.
            */
            <button
              type="button"
              className="knopf still"
              onClick={() => {
                setVorschlaege(null)
              }}
            >
              <X size={14} /> Liste schliessen
            </button>
          )}
        </div>
      )}

      {fehler && <p className="karte-warnung">{fehler}</p>}

      {vorschlaege?.length === 0 && <p className="leer">{leerSatz(quellen)}</p>}

      {vorschlaege && <Herkunft quellen={quellen} />}

      {vorschlaege && vorschlaege.length > 0 && offen.length === 0 && (
        <p className="leer">{schonImBuchSatz(schonDrin, offen.length)}</p>
      )}

      {offen.length > 0 && (
        <>
          <p className="tulpen-erklaerung">
            {offen.length === 1
              ? 'Ein Vorschlag aus der berechneten Strecke.'
              : `${String(offen.length)} Vorschläge aus der berechneten Strecke.`}{' '}
            Kilometer ab Etappenbeginn. Was schon eingetragen ist, bleibt; übernommen wird dazu.
            Alles hier ist eine Vermutung aus Daten — im Buch bleibt daneben Platz zum Berichtigen.
            {schonDrin > 0 && ` ${schonImBuchSatz(schonDrin, offen.length)}`}
          </p>

          <ul className="tulpenliste">
            {offen.map(({ vorschlag, lage: eintragsLage }, i) => (
              <li key={`${String(vorschlag.km)}-${vorschlag.shape}-${String(i)}`}>
                <span className="tulpen-km">{tulipKm(vorschlag.km)}</span>
                <TulipSketch tulip={vorschlag} />
                <span>
                  {/*
                    Der Haken sitzt beim Text und nicht in einer eigenen Spalte:
                    angetippt wird die ganze Zeile, und was sie sagt, steht
                    daneben - Zeichen, Kilometer, Hinweis. Genau das, was der
                    Nutzer bekommt, wenn er zustimmt.
                  */}
                  <label className="tulpen-wahl">
                    <input
                      type="checkbox"
                      checked={eintragsLage === 'neu'}
                      onChange={() => {
                        umschalten(vorschlag, eintragsLage === 'neu')
                      }}
                    />
                    <span className="tulpen-satz">{tulipText(vorschlag)}</span>
                  </label>
                  <small>{vorschlag.note}</small>
                  {eintragsLage === 'nein' && (
                    <small className="tulpen-abgesagt">
                      Schon einmal abgelehnt — ein Haken nimmt das zurück.
                    </small>
                  )}
                </span>
              </li>
            ))}
          </ul>

          <div className="knopfreihe">
            <button type="button" className="knopf" disabled={anzahl === 0} onClick={uebernehmen}>
              <Check size={14} />{' '}
              {anzahl === 0
                ? 'Nichts angehakt'
                : `${String(anzahl)} ${anzahl === 1 ? 'Tulpe' : 'Tulpen'} übernehmen`}
            </button>
          </div>
        </>
      )}
    </section>
  )
}

/**
 * Sagt, was von den vier Quellen gefragt werden konnte - und was nicht.
 *
 * Der Grund für diesen Absatz: eine leere Liste beantwortet zwei ganz
 * verschiedene Fragen gleich. „Auf dieser Etappe ist kein Tunnel“ und „für
 * diese Etappe gibt es keine Kartendaten“ sehen für den Nutzer identisch aus,
 * und nur eine der beiden Auskünfte darf er glauben. Dasselbe bei den Kehren:
 * eine Strecke aus der Zeit vor der Zählung hat keine, weil nie gezählt wurde.
 * Und beim Belag ist es der Regelfall: er wird eigens nachgeschlagen, und ohne
 * diesen Schritt gibt es keine Schotterzeile - was nicht heisst, dass es keinen
 * Schotter gibt.
 */
function Herkunft({ quellen }: { quellen: Quellen }) {
  const { kehren, abdeckung, belag } = quellen
  const saetze: string[] = []

  if (!kehren) {
    saetze.push(
      'Die gespeicherte Strecke stammt aus der Zeit vor der Kehrenzählung — dafür einmal alle Strecken neu berechnen lassen.',
    )
  }
  if (abdeckung === null) {
    saetze.push('Auf dem Server liegt kein Kartenarchiv, es wurde also nicht nach Tunneln gesucht.')
  } else if (abdeckung < ABDECKUNG_VOLLSTAENDIG) {
    saetze.push(
      `Das Kartenarchiv deckt nur ${String(Math.round(abdeckung * 100))} % dieser Strecke ab — auf dem Rest wurde nicht nach Tunneln gesucht.`,
    )
  }
  if (!belag) {
    saetze.push(
      'Der Belag wurde für diese Strecke noch nicht nachgeschlagen — im Block „Belag der Strecke“ oben lässt sich das anstossen.',
    )
  } else if (!belagGeprueft(belag)) {
    saetze.push(
      `Beim Belag liessen sich nur ${String(Math.round(belag.coverage * 100))} % dieser Strecke prüfen — auf dem Rest ist unbekannt, ob sie befestigt ist.`,
    )
  }

  if (saetze.length === 0) return null
  return <p className="tulpen-herkunft">{saetze.join(' ')}</p>
}
